import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDispatch } from 'react-redux';

import { QuoteItemDigiQuoteData } from '@frontend/api';
import {
  Box,
  Flex,
  PaperlessCubeIcon,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  ReorderIcon,
  Td,
  Text,
  Th,
  Tr,
} from '@frontend/components';

import {
  CUSTOM_DRAGGABLE_ITEM_TYPES,
  DROP_RESULT_TYPES,
  useDrag,
} from 'components/customDragLayer';
import type { DropResult } from 'components/customDragLayer';

import { formatLeadTimeLabel, formatPrice } from 'utils/formatters';

import { reorderOrderItems } from '../data/actions';
import type { SelectedOption } from '../data/types';

type ReviewOrderRowProps = {
  index: number;
  quoteItemId: number;
  selectedOption: SelectedOption;
  quoteItem: QuoteItemDigiQuoteData;
  shipsOn: string;
  toPreferredDateFormat: (date: string) => string;
};

const INDEX_COLUMN_BORDER = {
  borderLeft: '1px solid',
  borderLeftColor: 'separator.border',
} as const;

// overrides the table theme's _notLast.td border color for all cells in the row
const DARK_BOTTOM_BORDER_SX = {
  '&:not(:last-child) > td': {
    borderBottomColor: 'separator.border',
  },
} as const;

// same override but only for the index cell, so the addon separator stays light
const INDEX_CELL_DARK_BOTTOM_BORDER_SX = {
  '&:not(:last-child) > td:first-of-type': {
    borderBottomColor: 'separator.border',
  },
} as const;

export const ReviewOrderRow = ({
  index,
  quoteItemId,
  selectedOption,
  quoteItem,
  shipsOn,
  toPreferredDateFormat,
}: ReviewOrderRowProps) => {
  const dispatch = useDispatch();
  const rootComponent = quoteItem.rootComponent;
  const item = selectedOption.expedite ?? selectedOption.quantity;
  const leadTime =
    selectedOption.manualLeadTime ?? item.manualLeadTime ?? item.leadTime;

  const addOns = useMemo(
    () =>
      Object.entries(selectedOption.addOns).reduce<
        { addOnCellId: number; name: string; price: string }[]
      >((currAddOns, [addOnId, addOnCell]) => {
        const addOn = rootComponent.addOns.find(
          (addOn) => addOn.id.toString() === addOnId,
        );
        if (addOn) {
          currAddOns.push({
            addOnCellId: addOnCell.id,
            name: addOn.name,
            price: addOnCell.manualPrice ?? addOnCell.price,
          });
        }
        return currAddOns;
      }, []),
    [selectedOption.addOns, rootComponent.addOns],
  );

  const dragDescription: ReactNode = (
    <Flex gap={0.5} alignItems="center">
      <PaperlessCubeIcon boxSize={2.5} />
      <Text
        color="inherit"
        fontFamily="data"
        fontWeight="default"
        lineHeight="sm"
      >
        {rootComponent.partNumber || rootComponent.part.filename}
        {rootComponent.revision && (
          <>
            {' '}
            <Text
              as="span"
              color="inherit"
              fontFamily="inherit"
              fontWeight="inherit"
              fontSize="sm"
            >
              REV
            </Text>
            {rootComponent.revision}
          </>
        )}
      </Text>
    </Flex>
  );

  const handleDragEnd = useCallback(
    (
      _item: (typeof CUSTOM_DRAGGABLE_ITEM_TYPES)['ORDER_REVIEW_ITEM'],
      monitor: { getDropResult: () => DropResult | null },
    ) => {
      const dropResult = monitor.getDropResult();
      if (
        dropResult?.dropResultType ===
        DROP_RESULT_TYPES.REORDER_ORDER_REVIEW_ITEMS
      ) {
        const { newIndex } =
          dropResult as DropResult<'reorderOrderReviewItems'>;
        if (newIndex !== index) {
          const adjustedNewIndex = newIndex > index ? newIndex - 1 : newIndex;
          dispatch(
            reorderOrderItems({
              oldIndex: index,
              newIndex: adjustedNewIndex,
            }),
          );
        }
      }
    },
    [dispatch, index],
  );

  const [{ isDragging }, dragHandleRef, dragPreview] = useDrag({
    item: {
      draggableItemType: CUSTOM_DRAGGABLE_ITEM_TYPES.ORDER_REVIEW_ITEM,
      description: dragDescription,
      quoteItemId,
      index,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    onDragEnd: handleDragEnd,
  });

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const contentOpacity = isDragging ? 0.3 : 1;
  const hasAddOns = addOns.length > 0;
  const mainRowSx = hasAddOns
    ? INDEX_CELL_DARK_BOTTOM_BORDER_SX
    : DARK_BOTTOM_BORDER_SX;

  return (
    <>
      <Tr sx={mainRowSx}>
        <Th
          as="td"
          textAlign="end"
          fontWeight="default"
          fontFamily="data"
          verticalAlign="top"
          whiteSpace="nowrap"
          rowSpan={hasAddOns ? 2 : undefined}
        >
          {index + 1}
        </Th>
        <Td
          innerProps={{ padding: 0 }}
          verticalAlign="middle"
          {...INDEX_COLUMN_BORDER}
        >
          <Box
            ref={dragHandleRef}
            cursor="grab"
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={0.25}
            px={0.25}
          >
            <ReorderIcon size={2} color="feature.secondary" />
          </Box>
        </Td>
        <Td maxW={23} innerProps={{ padding: 0 }} opacity={contentOpacity}>
          <Part
            size="sm"
            variant="borderless"
            number={rootComponent.partNumber}
            rev={rootComponent.revision ?? undefined}
            filename={rootComponent.part.filename}
            border="none"
            flex={1}
          >
            <PartThumbnailContainer borderRadius={0}>
              <PartThumbnail
                partFileUuid={rootComponent.part.primaryFileId}
                filename={rootComponent.part.filename}
                isBoxShadowVisible
                containsVirus={rootComponent.part.containsVirus}
              />
            </PartThumbnailContainer>
            <PartLabel />
          </Part>
        </Td>
        <Td textAlign="end" opacity={contentOpacity}>
          {selectedOption.quantity.quantity}
        </Td>
        <Td textAlign="end" opacity={contentOpacity}>
          {formatLeadTimeLabel(leadTime, {
            unit: item.leadTimeDisplayUnit,
          }).toLowerCase()}
        </Td>
        <Td opacity={contentOpacity}>{toPreferredDateFormat(shipsOn)}</Td>
        <Td
          textAlign="end"
          fontFamily="data"
          fontWeight="heavy"
          opacity={contentOpacity}
        >
          {formatPrice(
            selectedOption.expedite?.unitPrice ??
              selectedOption.quantity.totalUnitPrice,
            rootComponent.unitPricePrecision,
          )}
        </Td>
        <Td
          textAlign="end"
          fontFamily="data"
          fontWeight="heavy"
          opacity={contentOpacity}
        >
          {formatPrice(item.totalPrice, rootComponent.unitPricePrecision)}
        </Td>
      </Tr>
      {hasAddOns && (
        <Tr sx={DARK_BOTTOM_BORDER_SX} opacity={contentOpacity}>
          <Th
            as="td"
            colSpan={6}
            fontWeight="default"
            pl={1}
            {...INDEX_COLUMN_BORDER}
          >
            {addOns.map((addOn) => (
              <Box key={addOn.addOnCellId}>{addOn.name}</Box>
            ))}
          </Th>
          <Th as="td" textAlign="end" fontWeight="default" fontFamily="data">
            {addOns.map((addOn) => (
              <Box key={addOn.addOnCellId}>
                {formatPrice(addOn.price, rootComponent.unitPricePrecision)}
              </Box>
            ))}
          </Th>
        </Tr>
      )}
    </>
  );
};
